import React from "react";

interface Change {
  version: string;
  content: React.ReactElement;
}

/**
 * Array ordered by version
 */
export const CHANGES: Change[] = [
  {
    version: '2.3',
    content: <div>
      <h2>2.3.0</h2>

      <h4>Support for new technologies</h4>
      <ul>
        <li><b>Server</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/server/monitoring/HPE-Morpheus/HPE-Morpheus-monitoring.php">HPE Morpheus VM Essentials</a></li>
            <li>Linux <a target="_blank" href="https://xormon.com/Linux-NVIDIA-GPU-monitoring.php">NVIDIA GPU</a> (via the OS agent)</li>
            <li>VMware <a target="_blank" href="https://xormon.com/VMware-vSAN-monitoring.php">vSAN</a>: performance metrics have been added</li>
            <li>Nutanix v4</li>
          </ul>
          <br />
        </li>

        <li><b>LAN</b>
          <ul>
            <li>Cisco <a target="_blank" href="https://xormon.com/LAN-performance-monitoring.php#CiscoFW">firewall</a></li>
            <li>FortiNet <a target="_blank" href="https://xormon.com/LAN-performance-monitoring.php#FortiGate">FortiGate</a></li>
          </ul>
          <br />
        </li>

        <li><b>Backup</b>
          <ul>
            <li>Veeam Backup & Replication v13 using the native Veeam REST API v1.3 (no need Veeam One)</li>
          </ul>
        </li>
      </ul>

      <h4>Major feature enhancements</h4>
      <ul>
        <li>New <a target="_blank" href="https://xormon.com/timeline.php">timeline</a> component for graphical presentation of health status over time</li>
        <li>Dynamically defined <a target="_blank" href="https://xormon.com/dashboard.php#dashboard-builder">dashboards</a> based on <a target="_blank" href="https://xormon.com/regular_expression.php">RegEx</a>. New items can be automatically included in dashboard groups</li>
        <li><a target="_blank" href="https://xormon.com/alerting.php#Prediction">Prediction</a> alerting</li>
        <li>New UI <a target="_blank" href="https://xormon.com/UI.php">colour schemes</a> (blue and light)</li>
        <li>A <a target="_blank" href="https://xormon.com/external-links.php">custom link</a> allows you to assign a web link to any item in the tool (VM, volume, etc.)</li>
      </ul>

      <h4>Other feature enhancements</h4>
      <ul>
        <li><b>Server</b>
          <ul>
            <li>IBM Power Systems
              <ul>
                <li><a target="_blank" href="https://xormon.com/IBM-Power-Systems-Resource-Groups.php">Resource Groups</a> support</li>
                <li><a target="_blank" href="https://xormon.com/reporter.php">Reporter</a>: IBM Power Enterprise Pools 2.0 support</li>
                <li><a href="AIX-WLM-monitoring.php">AIX WLM</a> support (through the <a target="_blank" href="https://xormon.com/server/monitoring/AIX/AIX-monitoring-agent.php">OS agent</a>)</li>
              </ul>
            </li>
            <li>VMware
              <ul>
                <li>New menu tree</li>
                <li>Windows RDM disks have been added to <a target="_blank" href="https://xormon.com/VMware-Topology.php">Topology</a></li>
              </ul>
            </li>
            <li>RHV, oVirt, OLVM
              <ul>
                <li>Added capacity graphs, topology, datacenter totals and logs pages</li>
                <li>Data from OS agents running there is mapped under VMs in the menu tree</li>
              </ul>
            </li>
            <li>XenServer, Nutanix: added datastore and disk capacity</li>
            <li>VMware, Proxmox, Nutanix, XenServer: Capacity datastore/disk <a target="_blank" href="https://xormon.com/Enhanced-Predictions.php">trends</a> have been added</li>
            <li>Linux: <a target="_blank" href="https://xormon.com/Linux-Topology.php">LVM Topology</a> enhancements</li>
            <li>Proxmox: added OS disk used % capacity tab</li>
            <br />
          </ul>
        </li>
        <li><b>OS Agent</b>
          <ul>
            <li>Solaris: added summary page for each CDOM, LDOM and Zone</li>
            <li>A new <a target="_blank" href="https://xormon.com/install-agent.php#AIX-Linux-XorMon">XorMon Linux OS agent</a> written in "GO" language has been released as a replacement for LPAR2RRD OS agent</li>
          </ul>
          <br />
        </li>
        <li><b>Storage</b>
          <ul>
            <li>IBM FlashSystem
              <ul>
                <li>Redesigned and much enhanced capacity presentation</li>
                <li><a target="_blank" href="https://xormon.com/IBM-FlashSystem-PBR.php">Replication policies</a> support </li>
                <li>new <a target="_blank" href="https://xormon.com/IBM-FlashSystem-volume-analysis.php">volume analysis</a> table (based on CLI cmd: lsvdiskanalysis)</li>
              </ul>
            </li>
            <li>IBM DS8000
              <ul>
                <li>Implemented the enhanced HW check for newer models (89XX/8AXXX)</li>
                <li>Optimized data download for storage devices with thousands of volumes</li>
              </ul>
            </li>
            <li>Veritas NetBackup
              <ul>
                <li>added jobs</li>
                <li>added tape statistics</li>
              </ul>
            </li>
            <li>Netapp FAS / AFF: monitoring of copy services</li>
            <li>Ceph: enhanced <a target="_blank" href="https://xormon.com/ceph-health-monitoring.php">health status</a> monitoring</li>
            <li>Hitachi VSP: fixed health status by removing lanbSummary section which caused false HW alerts on certain arrays</li>
          </ul>
          <br />
        </li>
        <li><b>Network</b>
          <ul>
            <li>SAN: HW event alerting: A hardware alert is generated whenever a port changes from a healthy (green) state to any other status</li>
            <li>LAN: <a target="_blank" href="https://xormon.com/LAN-Topology.php">Topology</a> was enhanced by including data from CDP protocol</li>
          </ul>
          <br />
        </li>
        <li><b>Tape library</b>
          <ul>
            <li>New global configuration table with all devices</li>
            <li>Implemented <a target="_blank" href="https://xormon.com/storage-grouping.php">grouping</a> of libraries in the menu</li>
          </ul>
          <br />
        </li>
        <li><b>Other technologies</b>
          <ul>
            <li>OracleDB got new graphs and tables for datafiles and tablespaces as well as alerts for them</li>
            <li>Red Hat OpenShift, Kubernetes : new <a target="_blank" href="https://xormon.com/RedHat-OpenShift-Topology.php">topology</a> diagrams</li>
            <li>HPE OneView: support for domains users (before only Local domain users)</li>
          </ul>
          <br />
        </li>
        <li><b>Product</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/storage-grouping.php">Grouping</a>: enhanced about sharing and possibility to use more levels</li>
            <li><a target="_blank" href="https://xormon.com/alerting.php#Event">HW alerting</a>: ability to exclude devices</li>
            <li>You can put your own <a target="_blank" href="https://xormon.com/UI.php#Title">name</a> of the tab in the browser per XorMon instance</li>
            <li>Connection test for all defined devices per a HW class</li>
            <li><a target="_blank" href="https://xormon.com/Resource-Configuration-Advisor.php">Advisor</a>: total CPU and Memory savings have been added</li>
            <li><a target="_blank" href="https://xormon.com/alerting.php">Alerting</a>: HW events can be directed to all users (before only admins could receive them)</li>
            <li><a target="_blank" href="https://xormon.com/Anomaly-alerting.php">Anomaly alerting</a>: major performance optimizations</li>
            <li>The trial period has been extended to three months</li>
            <li>The free edition: number of backup devices has been reduced to two (from five)</li>
          </ul>
          <br />
        </li>
      </ul>
      <p>
        Check the XorMon <a target="_blank" href="https://xormon.com/roadmap.php">roadmap</a> to see what else is coming soon
      </p>
    </div>
  },
  {
    version: '2.2.50',
    content: <div>
      <h2>2.2.50</h2>
      <h4>Support for new technologies</h4>
      <ul>
        <li><a target="_blank" href="https://xormon.com/storage/monitoring/EMC/EMC-PowerMax-VMAX-VNX-Unity-VPLEX-DataDomain-Isilon-XtremIO-CLARiiON-Celerra-monitoring.php#VPLEX">Dell EMC VPLEX</a></li>
        <li><a target="_blank" href="https://xormon.com/storage/monitoring/EMC/EMC-PowerMax-VMAX-VNX-Unity-VPLEX-DataDomain-Isilon-XtremIO-CLARiiON-Celerra-monitoring.php#Metronode">Dell EMC Metro Node</a></li>
      </ul>

      <h4>Feature enhancements (since <a target="_blank" href="https://xormon.com/note220.php">v2.2.30</a>)</h4>
      <ul>
        <li>NetApp StorageGrid: added error description to HW event alerting</li>
        <li>Windows VM and server: heatmaps have been added in the global Dashboard &#10145; Heatmap</li>
        <li>The free edition device <a target="_blank" href="https://xormon.com/support-ng.php">limitations</a> have been changed to:
          <ul>
            <li>IBM Power System: 1 HMC</li>
            <li>VMware: 1 vCenter</li>
            <li>4 storage devices</li>
            <li>4 SAN switches</li>
            <li>8 LAN switches</li>
          </ul>
        </li>
      </ul>

      <h4>Fixes (since <a target="_blank" href="https://xormon.com/note220.php">v2.2.30</a>)</h4>
      <ul>
        <li>Anomaly alerting: failed with "Anomaly Check : internal error", fixed</li>
        <li>Oversubscription disappeared from Total/Global tables, fixed</li>
        <li>Oracle DB: tablespace details disappeared , fixed</li>
        <li>DELL EMC PowerScale / Isilon: fixed incorrect total disk latency units (seconds instead of milliseconds)</li>
        <li>Kuberentes/Openshift fix memory usage % metric</li>
        <li>Adjustable default log interval</li>
      </ul>
      <p>
        Check the XorMon  <a target="_blank" href="https://xormon.com/roadmap.php">roadmap</a> to see what else is coming soon
      </p>
    </div>
  },
  {
    version: '2.2',
    content: <div>
      <h2>2.2.0</h2>
      <h4>Support for new technologies</h4>
      <ul>
        <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#IBMTS2k">IBM TS3k</a></li>
        <li><a target="_blank" href="https://xormon.com/server/monitoring/XenServer/XenServer-Citrix-performance-monitoring.php">XenServer / Citrix / XCP-ng</a></li>
        <li><a target="_blank" href="https://xormon.com/Server-service-procesor-monitoring.php#HPEOneView">HPE OneView</a></li>
      </ul>
      <p>
        Note: <b>Node.js v24</b> is required for this XorMon version, upgrade to it before XorMon v2.2.0 upgrade as per<a href="https://xormon.com/upgrade-nodejs-v24.php">this docu</a>.
      </p>
      <br />
      <h4>Feature enhancements</h4>
      <ul>
        <li><b>Server</b>
          <ul>
            <li>IBM Power Systems:
              <ul>
                <li>Fixed LPAR CPU usage in % for capped LPARs</li>
                <li>LAN <a target="_blank" href="https://xormon.com/IBM-Power-SAN-Topology.php">Topology</a> has been enhanced</li>
                <li>OS agent works now even without attached HMC, before OS agent data required attached HMC</li>
              </ul>
            </li>
            <li>VMware:
              <ul>
                <li><a target="_blank" href="https://xormon.com/VMware-Topology.php">Topology</a> new view: VMware ESXi ➡ Switch</li>
                <li>Topology: new view datastore ➡ volume</li>
              </ul>
            </li>
            <li>Proxmox
              <ul>
                <li><a target="_blank" href="https://xormon.com/Proxmox-Topology.php">Topology</a> enhanced</li>
                <li>Added <a target="_blank" href="https://xormon.com/Linux-pressure.php">Pressure</a> CPU, IO and Memory for VM/hosts</li>
              </ul>
            </li>
            <li>oVirt, OLVM, RHV: LAN usage summary added to host and cluster totals</li>
            <li>Service processor: added into <a target="_blank" href="https://xormon.com/Server-service-processor-Topology.php">SAN Topology</a></li>
            <li><a target="_blank" href="https://xormon.com/Resource-Configuration-Advisor.php">Advisor</a>: added Total CPU and memory savings to each page</li>
          </ul>
          <br />
        </li>
        <li><b>OS Agent</b>
          <ul>
            <li>Linux: added <a target="_blank" href="https://xormon.com/Linux-pressure.php">Pressure</a> CPU, IO and Memory</li>
            <li>AIX: NVME over FC support</li>
            <li>AIX: FC and  Ethernet statistic support for VIO Server 4.1.1.10+</li>
            <li>Solaris: runtime optimization for environments with many disks and zones on a host</li>
            <li>Linux on Power: fixed a bug where the number of configured CPUs was reported incorrectly</li>
          </ul>
          <br />
        </li>
        <li><b>Storage</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/SAN_Topology-storage.php">Topology</a>: Storage ➡ Server VM</li>
            <li>Netapp FAS / AFF:
              <ul>
                <li>Storage VMs added (both performance and capacity)</li>
                <li>Improved health status monitoring</li>
                <li>Volume snapshots added</li>
                <li>Volume inode usage percentage added</li>
                <li>Qtrees added (performance and quotas)</li>
              </ul>
            </li>
            <li>Pure Storage FlashArray:
              <ul>
                <li>Added Pool metric total reduction</li>
                <li>Added replication in/out and lag metrics for Pods</li>
                <li>Added support for asynchronous POD replication (<a target="_blank" href="https://xormon.com/copyservices-monitoring.ahp">mirroring</a>)</li>
                <li>Added filesystem physical &amp; logical capacity</li>
              </ul>
            </li>
            <li>Hitachi VSP:
              <ul>
                <li>Added configuration tables for Journal, Local Mirror, Remote Path Group, and Quorum Disks with status monitoring and alerting</li>
                <li>Added support for copy services (<a target="_blank" href="https://xormon.com/copyservices-monitoring.php">mirroring</a>) </li>
                <li>Added Pool metric data reduction</li>
              </ul>
            </li>
            <li>HPE MSA, Dell EMC ME4/5: added health check optimization: now checking both nodes and adding the system table per node to health status</li>
            <li>Infinidat InfiniBox: added to SAN Topology</li>
          </ul>
          <br />
        </li>
        <li><b>Container (Kubernetes, Red Hat OpenShift)</b>
          <ul>
            <li>Menu changes</li>
            <li>Monitoring stateful sets, deployments, replica sets, cron jobs and jobs</li>
            <li>Health status added</li>
            <li>Event log added</li>
            <li>Alert can be set for number of events in the event logs</li>
          </ul>
          <br />
        </li>
        <li><b>Network</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/SAN_Topology.php">SAN Topology</a>: new view: Server ➡ Switch</li>
            <li><a target="_blank" href="https://xormon.com/SAN_Topology.php">SAN Topology</a>: SAN all (Server ➡ SAN ➡ Storage/Tape)</li>
            <li><a target="_blank" href="https://xormon.com/ping-test.php">Ping</a> alerting: enhanced even for SAN and LAN switches</li>
            <li>SAN Brocade (REST API):
              <ul>
                <li>Fix the speed values for ports and fix Data In/Out [%] graphs</li>
                <li>Add the apiPort to the device configuration form</li>
                <li>Add the switch IP to the device configuration form (in case a hostname is used for data collection or the switch is behind a NAT)</li>
                <li>Add new <a target="_blank" href="https://xormon.com/SAN-Host-HBA-details.php">configuration table</a> for Fabric Device Management Interface (FDMI) and for HBAs</li>
              </ul>
            </li>
            <li>SAN Cisco: fix the portChannel ports that were missing in the XorMon UI</li>
            <li>SAN / LAN:
              <ul>
                <li>TOP is implemented</li>
                <li>Some switches might not work properly via SNMP properly since v2.1.0, fixed</li>
                <li>Add the serial number to the summary view</li>
              </ul>
            </li>
            <li>SAN: HW alert when any port goes from online to any else status (user settings)</li>
            <li>LAN Cisco: add the SFP module model to the configuration table for physical ports</li>
          </ul>
          <br />
        </li>
        <li><b>Tape</b>
          <ul>
            <li>IBM TS4k: added support for media 3592</li>
            <li>IBM TS4300: implemented Bearer token caching to prevent memory leak caused by frequent authentication</li>
          </ul>
          <br />
        </li>
        <li><b>Product</b>
          <ul>
            <li>REST API: added all CMDB configuration tables, <a target="_blank" href="https://xormon.com/public-api-example4.php">example</a></li>
            <li>Self monitoring: the internal DB data size usage graph is now available for each hardware class</li>
            <li><a target="_blank" href="https://xormon.com/search-application.php">Search</a>: enhanced, all switch port client WWN's and server WWN's are now included</li>
            <li>XorMon host CPU issues fixed, high CPU load could appear in older versions under certain circumstances</li>
            <li>Aggregated graphs: the legend of all graphs contains a "Total AVG/MAX" header row</li>
            <li><a target="_blank" href="https://xormon.com/alerting.php#zabbix">Zabbix</a> alerting integration</li>
            <li>Fixed several security issues reported by third-party security penetration testing</li>
          </ul>
          <br />
        </li>
        <p>
          Check the XorMon  <a target="_blank" href="https://xormon.com/roadmap.php">roadmap</a> to see what else is coming soon
        </p>
      </ul>
    </div>
  },
  {
    version: '2.1',
    content: <div>
      <h3>2.1.0</h3>

      <h4>Support for new technologies</h4>
      <ul>
        <li><a target="_blank" href="https://xormon.com/Backup-performance-monitoring.php#Commvault">Commvault Backup and Recovery</a></li>
        <li><a target="_blank" href="https://xormon.com/database/monitoring/MongoDB/MongoDB-monitoring.php">MongoDB</a></li>
        <li><a target="_blank" href="https://xormon.com/Linux-ZFS-monitoring.php">OpenZFS</a></li>
        <li>Tape Library:</li>
        <ul>
          <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#IBMTS2k">IBM TS2/3k</a></li>
          <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#Imation">Imation LR</a></li>
          <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#DellPowerVault">Dell PowerVault TL</a></li>
          <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#OverlandNEO">Overland NEO</a></li>
        </ul>
      </ul>

      <h4>Major feature enhancements</h4>
      <ul>
        <li><b>Server</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/Resource-Configuration-Advisor.php">Resource Configuration Advisor</a>: oVirt, Windows, Cloud, Kubernetess, RedHat OpenShift</li>
            <li>Red Hat OpenShift, Kubernetes: support monitoring of virtual machines (VMs)</li>
            <li>IBM Power Systems: HMC <a target="_blank" href="https://xormon.com/error-log-monitoring.php#HMC">system log</a> presentation</li>
            <li>Windows: mapping under virtualization and cloud technologies</li>
            <li>Solaris: filesystem usage graphs for CDOM/LDOM/zone</li>
          </ul>
          <br />
        </li>
        <li><b>Storage</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/Storage-snapshot.php">Snapshot</a> monitoring</li>
            <li>Dell EMC DataDomain VTL statistics</li>
            <li>Dell EMC Isilon: presentation of quotas for individual directories</li>
            <li>IBM FlashSystem: optimized for large eight-node clusters that may have problems with data gathering</li>
            <li>Pure Storage, Dell Compellent: fixed an issue where replicated volumes had duplicate volume UIDS matching the source volumes, causing database write errors</li>
          </ul>
          <br />
        </li>
        <li><b>Network</b>
          <ul>
            <li>SAN Broadcom (Brocade) REST API: a bug that prevented it from working under certain conditions has been fixed</li>
            <li>SAN Broadcom (Brocade) REST API: the fabric view has been fixed</li>
            <li>SAN, LAN (via SNMP): back-end queries to monitored devices have been reduced</li>
          </ul>
          <br />
        </li>
        <li><b>Product</b>
          <ul>
            <li><a target="_blank" href="https://xormon.com/CMDB.php">CMDB</a></li>
            <li>XorMon UI <a target="_blank" href="https://xormon.com/Phone-access.php">phone</a> optimization</li>
            <li>Dashboard: support for capacity tables</li>
            <li><a target="_blank" href="https://xormon.com/welcome-banner.php">Welcome banner</a> added to login page</li>
            <li>XorMon <a target="_blank" href="https://xormon.com/self-health-status-check.php">self-health status</a> check and alerting</li>
            <li>Auditing: logging of any user change or setup in the application</li>
            <li>Settings menu includes a <a target="_blank" href="https://xormon.com/Topology.php">topology</a> item that provides quick access to all topology diagrams</li>
            <li>AI: <a target="_blank" href="https://xormon.com/AI.php#MCP">Model Context Protocol</a> (MCP) server support</li>
          </ul>
          <br />
        </li>
      </ul>

    </div>
  },
  {
    version: '2.0',
    content: <div>
      <h3>2.0.0</h3>

      <p>
        Note: Data in weekly graphs needs to be migrated, and it might appear after some time (like one hour max) after the upgrade.
      </p>

      <div className="text-center">
        <iframe width="500" height="300" src="https://www.youtube.com/embed/wvgsQ7ZSVRo?si=PBLBo3Sv7dqh0mzK" title="XorMon v2.0" frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
      </div>
      <br />

      <h4>Support for new technologies</h4>
      <ul>
        <li>Storage
          <ul>
            <li>Qumulo</li>
            <li>Scality RING</li>
            <li>Hitachi HNAS</li>
            <li>Microsoft Storage Spaces Direct (S2D)</li>
            <li>IBM FlashSystem 900/840</li>
          </ul>
        </li>

        <li>Service processors
          <ul>
            <li><a target="_blank" href="https://xormon.com/Server-service-processor-monitoring-installation.php#DelliDRAC">Dell iDRAC</a></li>
            <li><a target="_blank" href="https://xormon.com/Server-service-processor-monitoring-installation.php#Xclarity">Lenovo XClarity</a></li>
          </ul>
        </li>

        <li>Tape libraries
          <ul>
            <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#IBM">IBM TS4500,4300</a></li>
            <li><a target="_blank" href="https://xormon.com/Tape-Library-monitoring.php#Quantum">Quantum Scalar</a></li>
          </ul>
        </li>
        <li>IBM Z Mainframe / IBM LinuxONE: DPM mode </li>
        <li>Broadcom (Brocade) SAN switch via <a target="_blank" href="https://xormon.com/install-network.php#SAN-RESTAPI">REST API</a></li>

      </ul>

      <h4>Major feature enhancements</h4>
      <ul>
        <li><a target="_blank" href="https://xormon.com/AI.php">AI agent</a></li>
        <li><a target="_blank" href="https://xormon.com/Anomaly-alerting.php">Anomaly</a> detection-based alerting</li>
        <li><a target="_blank" href="https://xormon.com/sso.php">Single sign-on</a> (SSO) support</li>
        <li><a target="_blank" href="https://xormon.com/favorites.php">Favorites</a>: new fast path to reach frequently used items</li>
        <li><a target="_blank" href="https://xormon.com/storage-grouping.php">Grouping</a> in the menu: IBM Power Systems, Linux, Windows, Server Service processor, LAN, SAN (Cisco only)</li>
        <li><a target="_blank" href="https://xormon.com/Resource-Configuration-Advisor.php">Resource Configuration Advisor</a>: new feature supported for IBM Power Systems, VMware, Proxmox, Nutanix</li>
        <li><a target="_blank" href="https://xormon.com/historical-reporting.php">Historical Reports</a>: a new, light and fast reporting feature</li>
        <li>IBM Power Systems: <a target="_blank" href="https://xormon.com/IBM-Power-CPU-Workload-Estimator.php">CPU Workload Estimator</a></li>
        <li>VMware: <a target="_blank" href="https://xormon.com/VMware-vSAN-monitoring.php">vSAN monitoring</a></li>
        <li>Broadcom (Brocade): Fabric and Zone configuration has been added (via the REST API)</li>
      </ul>

      <h4>Other feature enhancements</h4>
      <ul>
        <li>Server
          <ul>
            <li>IBM Power Systems: POWER11 and HMC 11 support</li>
            <li>IBM Power Systems: <a target="_blank" href="https://xormon.com/IBM-Power-SAN-Topology.htm">LAN Topology</a> has been added</li>
            <li>IBM Power Systems: enhanced LAN configuration (SRI-OV, vLAN, SEA, Link aggregation)</li>
            <li>IBM Power Systems: <a target="_blank" href="https://xormon.com/IBM-Power-CPU-monitoring.php#Pool">CPU Shared Pool</a> graphs have been enhanced</li>
            <li>IBM Power Systems: Virtual Ethernet performance graphs are now included in Total graphs (previously, only VIO were present) as well as VLAN under each LPAR</li>
            <li>VMware: vCenter 9.x support</li>
            <li>VMware: <a target="_blank" href="https://xormon.com/VMware-snapshot.php">snapshot</a> monitoring : list of all active snapshots their sizes and further details, VM snapshot size graphs</li>
            <li>VMware: <a target="_blank" href="https://xormon.com/VMware-Topology.htm">SAN HW topology</a> (ESXi to SAN switch)</li>
            <li>Proxmox: OS agent data mapping to VMs under Proxmox</li>
            <li>Consolidate tabs for VMs where all CPU, memory, disk, etc., tabs are grouped into sub-tabs to shorten the main tab menu</li>
          </ul>
        </li>

        <li>Storage
          <ul>
            <li>IBM FlashSystem: policy-based replication (PBR) and policy-based high availability (PBHA) support and its presentation</li>
            <li>IBM FlashSystem: new subsystems Host Cluster and Volume Groups</li>
            <li>Dell EMC² PowerMAX:</li>
            <ul>
              <li>Topology diagrams</li>
              <li>Alerts/Events</li>
              <li>Mapping between Host and Storage Group</li>
              <li>New capacity metrics Effective Total and Used Capacity (Unisphere V10+)</li>
            </ul>
            <li>Dell EMCÂ˛ PowerScale / Isilon: Pool: added spare capacity (VHS)</li>
            <li>Hitachi VSP: automatically groups port data into new 'Host Group' item</li>
            <li>Pure Storage: support for REST API 2.0 has been added, as well as for ports (Eth/FC) stats, volume group, and protection group</li>
            <li>The storage capacity table has been updated to include fill-in and growth rates, both of which are based on last week's data</li>
            <li>Oracle ZFS minor enhancements </li>
            <li>FreeNAS v25+ is now supported as the storage API has changed</li>
            <li>Storage grouping now allows for the creation of nested group levels, and storage can be in multiple groups</li>
          </ul>
        </li>
        <li>Network
          <ul>
            <li>SAN/LAN: Data in/out metrics are available in all graphs as Bytes/sec and, newly even as bits/sec</li>
            <li>SAN/LAN: added switch uptime</li>
            <li>SAN: added 'Total errors' graph</li>
            <li>LAN: new metrics 'Discards in/out'</li>
            <li>LAN: <a target="_blank" href="https://xormon.com/LAN_SFP-Power.php">SFP Power</a> graphs</li>
            <li>LAN: new interswitch link graphs (ISL), all ISLs from all switches in a single graph</li>
          </ul>
        </li>
        <li>OS agent
          <ul>
            <li>Linux: added presentation of <a target="_blank" href="https://xormon.com/error-log-monitoring.php#Linux">Linux error log</a> (dmesg)</li>
            <li>Linux: <a target="_blank" href="https://xormon.com/NFS-monitoring.php">NFS client/server</a> statistics  have been added based on the nfsiostat command</li>
            <li>Linux: <a target="_blank" href="https://xormon.com/Logical-Volume-Manager.php">LVM</a> configuration and topology have been added</li>
            <li>OS agent graphs are mapped under the database instances on which they are running</li>
          </ul>
        </li>

        <li>Service Processor
          <ul>
            <li>Service Processor (HPE iLO): has been enhanced about CPU, Memory Bus, I/O Bus usage graphs</li>
            <li>Service Processor: HW inventory greatly enhanced</li>
          </ul>
        </li>

        <li>Product
          <ul>
            <li>Summary pages: graph's legends are hidden by default and can be switched on by clicking a button in the top right</li>
            <li>Summary pages: a custom time range has been added</li>
            <li><a target="_blank" href="https://xormon.com/grafana.php">Grafana</a>: possibility to import tables and further minor enhancements</li>
            <li>VM TOP: a custom time range has been added</li>
            <li>Topology exports to PNG</li>
            <li>Search feature (top-right magnifier icon) has been extended</li>
            <li>Weekly and Monthly graphs have higher resolution, lower time aggregation is used</li>
            <li><a target="_blank" href="https://xormon.com/zoom.php">Zoom</a>: it is not only vertical, but even horizontal, useful especially for filtering huge peaks in graphs</li>
            <li>Reporter/Exporter: OS agent metrics are mapped to virtualization technology directly during report creation</li>
            <li>REST API has been extended
              <ul>
                <li>alerting: list active (triggered) and not active alerts</li>
                <li>certificate management</li>
                <li>storage capacity metrics have been enhanced</li>
              </ul>
            </li>
            <li>Reporter:
              <ul>
                <li>Ability to generate graphs with multiple metrics</li>
                <li>Option to sort tables by column</li>
                <li>Ability to generate more complicated tables</li>
              </ul>
            </li>
          </ul>
        </li>

        <li>
          Others
          <ul>
            <li>IBM Storage Protect (TSM): drive usage graph</li>
            <li>Oracle DB: improved menu layout</li>
          </ul>
        </li>
      </ul>
      ... and many many minor enhancements and bug fixes.
    </div>
  },
  {
    version: '1.9',
    content: <div>
      <h3>1.9.0</h3>

      <h4>Support for new technologies</h4>
      <ul>
        <li>IBM Storage Scale (GPFS)</li>
        <li>IBM Elastic Storage Server (ESS)</li>
        <li>Pure Storage FlashBlade</li>
        <li><a href="https://xormon.com/Skytap-performance-monitoring.php" target="_blank">Skytap</a></li>
        <li><a href="https://xormon.com/Backup-performance-monitoring.php#Veeam" target="_blank">Veeam Backup & Replication</a></li>
        <li><a href="https://xormon.com/Server-service-procesor-monitoring.php" target="_blank">Server service</a> processor monitoring (HPE iLO)</li>
        <li>VMware Tanzu</li>
      </ul>

      <h4>Major feature enhancements</h4>
      <ul>
        <li><a href="https://xormon.com/ping-test.php" target="_blank">VM availability</a> checks via 'ping'</li>
        <li>A <a href="https://xormon.com/Summary.php" target="_blank">summary page</a> with performance graphs, configuration tables and alerting data for every monitored item such as  VM, storage, server ...</li>
        <li>AIX: filesystem <a href="https://xormon.com/IBM-Power-SAN-Topology.php" target="_blank">topology</a> mapping AIX (Filesystem &#10145; Volume Group &#10145; Disk &#10145; Storage &#10145; Pool &#10145; Volume)</li>
        <li>AIX: <a href="https://xormon.com/error-log-monitoring.php" target="_blank">error log</a> collection and presentation in the UI (requires LPAR2RRD OS agent 8.1.0+)</li>
      </ul>

      <h4>Minor feature enhancements</h4>
      <ul>
        <li>Storage
          <ul>
            <li>Storage <a href="https://xormon.com/copyservices-monitoring.php" target="_blank">copy services</a> alerting has been implemented</li>
            <li>Storage <a href="https://xormon.com/copyservices-monitoring.php" target="_blank">copy services</a> were improved regarding freeze time</li>
            <li>IBM FlashSystem: support for child pools as a new subsystem</li>
            <li>IBM DS8k, FlashSystem: <a href="https://xormon.com/Storage_sfp_power_supported_devices.php" target="_blank">SFP Power</a> graphs added</li>
            <li>Dell EMC Isilon: added tier monitoring (active and cloud)</li>
            <li>Huawei OceanStor, Dorado, Pacific, OceanProtect, OceanDisk, A-series : LDAP user authentication support</li>
            <li>IBM Storage Protect (TSM): added SUR usage metrics for IBM Spectrum Protect Suite (SUR) licensed servers</li>
          </ul>
        </li>
        <li>Network
          <ul>
            <li>SAN: <a href="https://xormon.com/SAN_SFP-Power.php" target="_blank">SFP Power</a> Total graphs were added</li>
            <li>LAN: ability to group LAN switches into user defined groups with presentation totals per a group</li>
          </ul>
        </li>
        <li>OS agent
          <ul>
            <li>IBM i OS agent: now includes <a href="https://xormon.com/multipath.php" target="_blank">disk multipath</a> information, similar to AIX/Linux (upgrade to agent v1.9.0)</li>
            <li>AIX/Linux OS agent: sends connected data to the server randomly every 10 - 19 mins (before 20 - 29 mins)</li>
            <li>AIX/Linux 'JOB' & IBMi 'WRKACTJOB' data retention increased from 7 to 30 days, added new monthly graphs</li>
          </ul>
        </li>
        <li>Cloud
          <ul>
            <li>AWS: OS agent mapping for running AWS EC2 Linuxes (OS agent data mapped to the AWS ESC2 menu structure)</li>
            <li>AWS: support for RDS and API</li>
            <li>Google Cloud: OS agent mapping</li>
            <li>IBM Cloud: many enhancements especially in CPU monitoring (requires LPAR2RRD OS agent 8.1.0+)</li>
          </ul>
        </li>
        <li>Server
          <ul>
            <li>VMware Custom Groups: added a VM capacity table and configuration</li>
            <li>VMware: <a href="https://xormon.com/VMware-CPU-monitoring.php#CPUCoStop" target="_blank">CPU CoStop</a> monitoring</li>
            <li>VMware: CPU core graphs under ESXi</li>
            <li>VMware VM Memory graph: added memory allocation</li>
          </ul>
        </li>
        <li>Product
          <ul>
            <li><a href="https://xormon.com/exporter.php" target="_blank">Exporter</a>: added the ability to select MAX, AVG, or LAST values per a defined time period</li>
            <li>HW alert history log added</li>
            <li>All tables can be exported directly to CSV, PDF, or JSON. If you use filtering, only filtered data will be exported</li>
            <li><a href="https://xormon.com/proxy-agent.php" target="_blank">Proxy</a>: table showing the status of proxy syncing and its alerting if there has been no new data for the last hour</li>
            <li><a href="https://xormon.com/CFG-Tracker.php" target="_blank">CFG Tracker</a>: ability to set and save filters</li>
            <li>Kubernetes, VMware Tanzu, RedHat OpenShift: decreased max number of monitored clusters in the free edition from 8 to 4</li>
          </ul>
        </li>
      </ul>
      Numerous minor enhancements and bug fixes have been made.<br />
      <br />
      You can check what is going on soon in XorMon in the <a href="https://xormon.com/roadmap.php" target="_blank">roadmap</a>
    </div>
  },
  {
    version: '1.8',
    content: <div>
      <h3>v1.8.0 (20th Feb 2025)</h3>

      <h4>Support for new technologies</h4>
      <ul>
        <li>IBM Z Mainframe</li>
        <li>Veritas NetBackup</li>
        <li>ExaGrid Tiered Backup Storage</li>
        <li>iXsystems FreeNAS, TrueNAS</li>
        <li>Hitachi Virtual Storage Platform One (VSP One) Block</li>
        <li>Fujitsu ETERNUS</li>
      </ul>

      <h4>Feature enhancements</h4>
      <ul>
        <li><a href="https://xormon.com/grafana.php" target="_blank">Grafana</a> integration</li>
        <li>Filtering in the left tree menu</li>
        <li>Reporter: ability to exclude items from selections</li>
        <li><a href="https://xormon.com/filesystem-monitoring.php" target="_blank">Filesystem usage</a> graphs (total,used,free), integration with alerting, reporting and exporting</li>
        <li><a href="https://xormon.com/users.php" target="_blank">Users</a>: read-only role, user locking</li>
        <li>SSL communication support between the OS agent and XorMon server (use -x when you running the OS agent)</li>
        <li>VMware: mapping table: VM disks ➡ datastore and storage devices</li>
        <li>IBM i OS agent: implemented support for reporting, exporting and alerting</li>
        <li>Storage capacity prediction graphs: implemented total capacity per storage device (previously only per pool prediction was available)</li>
        <li>Volume multipath: storage volumes assigned to VMs are visible in the "Volume" VM tab</li>
        <li>Item Select: ability to exclude items from already selected set of items</li>
        <li>UI <a href="https://xormon.com/search-application.php" target="_blank">Search</a> feature enhanced with IP, MAC, WWN and Volume ID search capabilities</li>
        <li>Dell EMC Power MAX: support for 10.2+ firmwares</li>
        <li>Tag support in the REST API</li>
        <li>SSL communication support between the OS agent and XorMon server (use -x when you running the OS agent)</li>
        <li>OS agent sends MAC and WWN of all interfaces, they are accessible through the UI Search feature</li>
        <li>Broadcom SANnav: events and zoning setup are presented in the application</li>
        <li>IBM FlashSystem / Storwize / SVC: storage firmware 8.7+ support, older XorMon versions do not support it</li>
        <li>IBM Flash System / Storwize: support for <a href="https://xormon.com/copyservices-monitoring.php" target="_blank">copy services</a> (mirroring)</li>
      </ul>

      <h4>Many fixes especially for</h4>
      <ul>
        <li>IBM Storage Protect (TSM): include many smaller enhancements</li>
        <li>Hitachi HCP</li>
        <li>VMware</li>
        <li>Hitachi VSP</li>
      </ul>

      <h4>Licensing changes</h4>
      <ul>
        <li>Storage: 8 storage devices limit (it was 12)</li>
        <li>Backup devices: 4 devices limit (it was 1)</li>
      </ul>

    </div>
  },
  {
    version: '1.7',
    content: <div>
      <h3>v1.7.0 (10th Dec 2024)</h3>

      <h4>Support for new technologies</h4>

      <ul>
        <li>IBM Cloud</li>
        <li>IBM Storage Protect (formerly IBM Spectrum Protect, TSM)</li>
        <li>IBM DS8000</li>
        <li>Ceph</li>
        <li>IBM Storage Defender Data Protect</li>
        <li>HPE Alletra MP</li>
      </ul>

      <h4>Feature enhancements</h4>

      <ul>
        <li><a href="https://xormon.com/dark-mode.php" target="_blank">UI dark mode</a> as a user option</li>
        <li><a href="https://xormon.com/search.php" target="_blank">UI search</a> per IP address</li>
        <li><a href="https://xormon.com/CFG-Tracker.php" target="_blank">CFG Tracker</a>: easy tracking all configuration changes mainly on VMs, containers and volumes</li>
        <li><a href="https://xormon.com/IBM-Power-SAN-Topology.php" target="_blank">IBM Power Topology</a>: LPAR ➡ VIO ➡ FC adapter ➡ SAN switch</li>
        <li><a href="https://xormon.com/VMware-Topology.php" target="_blank">VMware Topology</a>: schemas are presented on cluster, datastore and VM levels</li>
        <li><a href="https://xormon.com/Nutanix-Topology.php" target="_blank">Nutanix Topology</a> schemas</li>
        <li>Nutanix: capacity per cluster, host and disk</li>
        <li>VMware: TOP Datastore: Used Space % added</li>
        <li>Microsoft Azure agent 2 support</li>
        <li><a href="https://xormon.com/vm-migration.php" target="_blank">VM migration</a> CPU graphs for IBM Power System, Nutanix and Proxmox</li>
        <li><a href="https://xormon.com/alerting.php#builder" target="_blank">Alerting</a>: new API for user defined connections to 3rd party tools</li>
        <li><a href="https://xormon.com/graphing.php" target="_blank">Graphs</a>: switch between stacked and line graphic mode in each graph</li>
        <li>Filesystem global tables per technology</li>
        <li>ACL enhancements, possibility to export/import ACL rules, rules are available via the REST API</li>
        <li>EMC Unity: total is now block + FS (before FS was not considered in total)</li>
        <li>Dell EMC PowerMAX: <a href="https://xormon.com/SAN_Topology-storage.php" target="_blank">Topology</a> support</li>
        <li>Dell EMC Isilon: add node table to configuration page</li>
        <li>Dell Compellent: add back-end statistics for Pool (IO, DATA)</li>
        <li><a href="https://xormon.com/copyservices-monitoring.php" target="_blank">Copy services</a> (mirroring) support for:</li>
        <ul>
          <li>HPE 3PAR / Alletra 9000 / Alletra MP</li>
          <li>Huawei OceanStor / Dorado</li>
          <li>IBM DS8000</li>
        </ul>
        <li>Significant speed up of loading aggregated graphs with many items (3 - 6x faster now)</li>
      </ul>

      Many other small changes and bug fixes were done.
    </div>
  },
  {
    version: '1.6',
    content: <div>
      <h3>v1.6.0 (19th Sep 2024)</h3>
      The main enhancements include support for new monitored devices and further feature improvements.
      <h4>Support for new technologies</h4>
      <ul>
        <li>IBM Power Enterprise Pools 2.0 (PEP2.0)</li>
        <li>oVirt / Red Hat Virtualization (RHV) / OLVM</li>
        <li>Solaris CDOM / LDOM / Zone</li>
        <li>Broadcom (Brocade) SANnav</li>
        <li>Synology</li>
        <li>Dell EMC² Unity</li>
        <li>DataCore SANsymphony</li>
        <li>Huawei OceanStor Pacific</li>
        <li>Huawei OceanStor Pacific NAS</li>
        <li>Huawei OceanProtect Backup Storage</li>
      </ul>
    </div>
  },

] as const;